/**
 * WorkPanel — 강의 작업 화면의 작업 칸(설계 FE-20260912 §W01·§W03·§W04·§W06, FE-20260913 §2·§6).
 *
 * 고른 줄이 작업의 중심이다. 조건(자리·이름·빠른 조건·[조건 되돌리기]·[저장]·[다시 검색])과 실제 문제 결과를 함께 둔다.
 * 화면 글자에 「원본」을 쓰지 않는다(오너 2026-09-13 「어색하다」) — 서버에 저장된 것은 「저장된 조건」이다.
 * [조건 되돌리기]는 이 줄 초안을 버리고 저장된 조건으로 돌아가고, [저장]은 이 줄 조건을 서버에 저장한다.
 * 배치는 작업 칸의 폭으로 가른다(FE-20260913 §6):
 *   - 두 칸 — 작업 칸 폭이 836px(안쪽 804) 이상이면 왼쪽 조건 | 오른쪽 결과(488px, 문제 v2 보기 칸과 같은 값).
 *     16px 덜 좁아져야 위아래로 돌아간다(스크롤바가 생기고 사라지며 두 배치를 오가지 않게, 문제 v2 와 같은 방식).
 *     두 칸이 따로 굴러간다. [상세 조건]은 왼쪽 칸 안에서 펼치고 결과는 그대로 둔다.
 *   - 위아래 — 위(고정)는 조건, 아래(스크롤)는 결과. [상세 조건]이면 결과 자리에 묶음 편집기(W04).
 *
 * 조건이 없는 줄(제목 줄)도 같은 칸을 쓴다 — 빠른 조건이 처음부터 열려 있어 개념·라벨·난이도를 고르면 그 줄의 조건 초안이
 * 된다(오너 요청 2026-09-13 「하위 폴더 등의 정보를 표출할 필요는 없어」). 하위 줄 목록은 두지 않는다 — 나무가 이미 보인다.
 * 조건이 없는 동안은 검색하지 않는다(query:null 검색은 전체 검색이다). 빈 강의만 따로 — [첫 조건행 만들기]·[제목행 만들기].
 *
 * 이름은 초안이 아니다 — Enter·blur 에 곧바로 저장한다(오너 결정 2026-09-13). 조건만 줄별 초안이고 [저장]으로 서버에 간다.
 * 검색은 저절로 한다(설계 FE-20260913 §5 · 오너 결정 D4) — 줄을 고르면 훅(select)이 한 번, 같은 줄에서 식이 바뀌면 이 칸이
 * 0.8초 뒤 한 번. 0.8초 안에 또 바뀌면 마지막 식 하나만 찾고, 원래 식으로 돌아오면(받아 둔 결과가 최신) 찾지 않는다.
 * [다시 검색]은 기다리지 않고 곧바로 찾는다(같은 식이어도 새로 받는다). 쪽 이동도 곧바로다.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Search, RotateCcw, MoreHorizontal, AlertTriangle, Plus, X,
} from 'lucide-react';
import { Button } from '../common';
import { VIEW_PANE_PX } from '../ProblemHubV2/viewPaneWidth';
import FloatingPanel from './FloatingPanel';
import QuickConditions from './QuickConditions';
import QueryEditor from './QueryEditor';
import { exprKey } from './workspaceModel';
import SearchResults, { searchedExprOf } from './SearchResults';
import CommonConditionBar from './CommonConditionBar';
import CommonApplyDialog from './CommonApplyDialog';
import { planCommonApply, commonOriginOf } from './commonConditions';
import { inspectExpr } from './syllabusQuery';
import {
  newExpr, hasEmptyGroup, clampGroup, summarizeExpr,
} from './quickExpr';
import { pathNames, moveTargets, descendantsOf } from './treeIndex';
import { makeNames } from './names';
import MathName, { MathPath } from './MathName';

const panelCls = 'flex h-full min-h-0 flex-col rounded-3xl bg-white shadow-lg';
const menuRowCls = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40';
const handleCls = 'shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600';

/**
 * 두 칸의 치수(px) — 근거는 설계 FE-20260913 §6.
 *   300 왼쪽 조건 칸의 최소 폭 — 난이도 다섯 칸·[제한 없음]·버튼 셋이 두 줄 안에 드는 폭(설계의 가정값, 실측으로 고친다)
 *   16  작업 칸 네 변 여백(p-4) · 칸 사이 틈(gap-4)
 *   488 오른쪽 결과 칸 = 문제 v2 보기 칸(VIEW_PANE_PX) — 카드 본문이 문제 v2 와 같은 자리에서 꺾인다
 * 문턱은 작업 칸 전체 폭으로 잰다: 16 + 300 + 16 + 488 + 16 = 836(안쪽 804). 820 아래로 내려가야 위아래로 돌아간다.
 */
export const CONDITION_MIN_PX = 300;
const EDGE_PX = 16;
export const SPLIT_ENTER_PX = EDGE_PX + CONDITION_MIN_PX + EDGE_PX + VIEW_PANE_PX + EDGE_PX;
export const SPLIT_LEAVE_PX = SPLIT_ENTER_PX - 16;

/** 조건을 바꾼 뒤 검색까지 기다리는 시간 — 연속으로 누르면 마지막 식 하나만 찾는다(설계 FE-20260913 §5) */
export const AUTO_SEARCH_MS = 800;

/**
 * 작업 칸의 폭을 재서 두 칸인지 정한다(ProblemHub useSplitLayout 과 같은 판정). 칸 폭은 화면·사이드바·나무 폭이 함께 정해
 * 미디어쿼리로는 못 잰다. 콜백 ref 라 붙는 요소가 바뀌면 새 요소를 잰다. 끊기도 콜백이 null 을 받을 때 한다 — effect 치우기로
 * 끊으면 개발 모드(StrictMode)가 effect 를 걷었다 세울 때 감시만 끊기고 ref 는 다시 불리지 않는다(Syllabus.js measureRef 와 같은 함정).
 * ResizeObserver 가 없거나 폭이 0 이면 위아래다 — 검사 환경(jsdom)이 여기 해당해 위아래 검사가 그대로 돈다.
 */
function useSplitLayout() {
  const [twoCol, setTwoCol] = useState(false);
  const observerRef = useRef(null);
  const ref = useCallback((el) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      setTwoCol((prev) => (prev ? w >= SPLIT_LEAVE_PX : w >= SPLIT_ENTER_PX));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    observerRef.current = ro;
  }, []);
  return [ref, twoCol];
}

/**
 * 줄 이름 — 누르면 그 자리 입력칸. Enter·blur 에 곧바로 저장, Escape 는 취소.
 * 보일 때는 이름 속 $…$ 를 수식으로 그리고(MathName, 제목이라 큰 분수도 그대로), 입력칸 · title 은 원문이다
 */
function RowName({
  name, editable, onRename, autoEdit = false, onAutoEditDone,
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => { if (!editing) setText(name); }, [name, editing]);
  useEffect(() => {
    if (autoEdit && editable) { setEditing(true); if (onAutoEditDone) onAutoEditDone(); }
  }, [autoEdit, editable]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
    doneRef.current = false;
  }, [editing]);

  const commit = async () => {
    if (doneRef.current) return;
    const next = text.trim();
    if (!next || next.length > 200 || next === name) { doneRef.current = true; setEditing(false); setErr(null); return; }
    setBusy(true);
    setErr(null);
    try {
      await onRename(next);
      doneRef.current = true;
      setEditing(false);
    } catch (e) {
      setErr(e?.message || '이름을 저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <h2 className="min-w-0 flex-1 truncate text-base font-bold text-gray-900" title={name}>
        {editable ? (
          <button type="button" onClick={() => setEditing(true)} title="눌러서 이름 바꾸기 — Enter 로 저장" className="max-w-full truncate rounded-lg text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <MathName text={name} />
          </button>
        ) : <MathName text={name} />}
      </h2>
    );
  }
  return (
    <div className="min-w-0 flex-1">
      <input
        ref={inputRef}
        type="text"
        value={text}
        disabled={busy}
        maxLength={200}
        aria-label="행 이름"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); doneRef.current = true; setEditing(false); setErr(null); }
        }}
        onBlur={commit}
        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-base font-bold text-gray-900 focus:border-primary focus:outline-none disabled:bg-gray-50"
      />
      {err && <p role="alert" className="mt-1 text-xs text-red-600">{err}</p>}
    </div>
  );
}

/** 줄 관리 — 옮기기 · 제목행 전환 · 지우기(드문 동작이라 더보기로, 설계 §W01) */
function RowManageMenu({
  node, index, owner, onReparent, onConvert, onDelete, busy, openMove = false, onOpenMoveDone,
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('menu');
  const btnRef = useRef(null);
  // 나무의 줄 더보기 [옮기기…] 가 이 줄을 고른 뒤 여기 옮기기 목록을 곧바로 연다(한 벌의 목록만 둔다)
  useEffect(() => {
    if (!openMove || !owner) return;
    setView('move');
    setOpen(true);
    if (onOpenMoveDone) onOpenMoveDone();
  }, [openMove, owner]); // eslint-disable-line react-hooks/exhaustive-deps
  const targets = useMemo(() => (open && view === 'move' ? moveTargets(index, node.node_id) : []), [open, view, index, node.node_id]);
  if (!owner) return null;
  const close = () => { setOpen(false); setView('menu'); };
  const isTitle = node.query == null;
  return (
    <>
      <button ref={btnRef} type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700">
        행 관리 <MoreHorizontal className="size-3.5" />
      </button>
      {open && (
        <FloatingPanel anchorRef={btnRef} align="end" onClose={close} label={`${node.name} 행 관리`} className="w-60">
          {view === 'menu' ? (
            <div className="space-y-0.5">
              <button type="button" disabled={busy} onClick={() => setView('move')} className={`${menuRowCls} text-gray-700 hover:bg-gray-50`}>다른 행 아래로 옮기기…</button>
              <button type="button" disabled={busy} onClick={() => { close(); onConvert(node); }} className={`${menuRowCls} text-gray-700 hover:bg-gray-50`}>
                {isTitle ? '이 행에 조건 만들기' : '제목행으로 바꾸기(조건 떼기)'}
              </button>
              <button type="button" disabled={busy} onClick={() => { close(); onDelete(node); }} className={`${menuRowCls} text-danger hover:bg-red-50`}>지우기(하위 행 포함)</button>
            </div>
          ) : (
            <div>
              <p className="px-2 pb-1 pt-0.5 text-[11px] text-gray-500">어느 행 아래로 옮길까요? 옮기면 그 행의 맨 끝에 섭니다.</p>
              <ul className="max-h-64 space-y-0.5 overflow-y-auto">
                {targets.map((t) => (
                  <li key={t.id ?? 'root'}>
                    <button
                      type="button"
                      disabled={busy || String(t.id) === String(node.parent_id ?? null) || (t.id == null && node.parent_id == null)}
                      onClick={() => { close(); onReparent(node, t.id); }}
                      style={{ paddingLeft: `${8 + Math.max(0, t.depth - 1) * 12}px` }}
                      className={`${menuRowCls} text-gray-700 hover:bg-gray-50`}
                    >
                      <span className="min-w-0 flex-1 truncate">{t.id == null ? '(최상위)' : <MathName text={t.name} compact />}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </FloatingPanel>
      )}
    </>
  );
}

function WorkPanel({
  ws,
  node,
  dict,
  labelNames,
  onRememberLabels,
  onStartNewRow,
  onCreateConditionRow,
  onReparent,
  onConvert,
  onDelete,
  renameRequest = null,
  onRenameRequestDone,
  moveRequest = null,
  onMoveRequestDone,
  sheet = false,
  topSlot = null,
  scrollMemory = null,
}) {
  const names = useMemo(() => makeNames(dict, labelNames), [dict, labelNames]);
  const id = node ? String(node.node_id) : null;
  const owner = ws.owner;
  const [groupIdx, setGroupIdx] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [askSearch, setAskSearch] = useState(null); // { id, page } — 초안이 커밋된 다음 렌더에서 검색
  const localScrollMemory = useRef(new Map());
  const [splitRef, twoCol] = useSplitLayout();
  const resultsRef = useRef(null); // 두 칸 배치의 결과 칸 — 개념 목록 창이 이 칸은 가리지 않는다(설계 FE-20260913 §2-1)
  // 공통 조건 반영 창 — { mode: 'save'|'apply', plan }. 여는 순간 계획을 얼린다(진행 중 원본이 바뀌어도 창의 행이 흔들리지 않게)
  const [applyState, setApplyState] = useState(null);

  // 줄이 바뀌면 묶음 고르기·상세 편집을 처음으로
  useEffect(() => { setGroupIdx(0); setDetailOpen(false); }, [id]);

  const { search } = ws;
  useEffect(() => {
    if (!askSearch) return;
    search(askSearch.id, { page: askSearch.page });
    setAskSearch(null);
  }, [askSearch, search]);

  // ── 자동 검색 — 같은 줄에서 식(정규화 열쇠)이 바뀌면 0.8초 뒤 한 번(설계 FE-20260913 §5) ──
  // 줄을 고른 순간의 검색은 훅(select)의 몫이다 — 여기서는 그 줄의 기준 열쇠만 잡는다. 기준과 같은 식이면(개발 모드의
  // 두 번 부르기 · 식과 무관한 다시 그리기) 아무것도 하지 않는다. 받아 둔 결과가 지금 식의 것이면(원래 식으로 되돌림) 찾지 않는다.
  const liveQuery = id != null && ws.data ? ws.queryOf(id) : null;
  const liveInsp = liveQuery == null ? null : inspectExpr(liveQuery, { mode: 'search' });
  const liveKey = liveInsp?.ok ? JSON.stringify(liveInsp.normalized) : null;
  const liveFresh = id != null && ws.data ? ws.freshnessOf(id) : 'none';
  const [autoPending, setAutoPending] = useState(false);
  const autoRef = useRef({ id: null, key: null, timer: null });
  useEffect(() => {
    const prev = autoRef.current;
    if (prev.id === id && prev.key === liveKey) return;
    clearTimeout(prev.timer);
    autoRef.current = { id, key: liveKey, timer: null };
    setAutoPending(false);
    if (prev.id !== id || liveKey == null || liveFresh === 'fresh') return;
    setAutoPending(true);
    autoRef.current.timer = setTimeout(() => {
      autoRef.current.timer = null;
      setAutoPending(false);
      search(id, { page: 1 });
    }, AUTO_SEARCH_MS);
  }, [id, liveKey]); // eslint-disable-line react-hooks/exhaustive-deps -- 줄·식이 바뀔 때만 센다
  useEffect(() => () => clearTimeout(autoRef.current.timer), []);

  if (!ws.data) return null;

  // ── 빈 강의(설계 §W06) ──
  if (!ws.items.length) {
    return (
      <div className={`${panelCls} items-center justify-center p-6 text-center`}>
        {topSlot}
        <p className="text-sm font-bold text-gray-900">이 강의에는 아직 행이 없습니다</p>
        <p className="mt-1 text-xs text-gray-500">조건행은 문제를 찾고, 제목행은 진도를 묶습니다. 행 이름은 나무에서 치고 Enter 로 바로 저장됩니다.</p>
        {owner ? (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button size="sm" onClick={() => onCreateConditionRow(null, null)} disabled={ws.pendingWrites > 0}>
              <Plus className="mr-0.5 size-3.5" /> 첫 조건행 만들기
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onStartNewRow({ parentId: null, afterId: null })}>
              <Plus className="mr-0.5 size-3.5" /> 제목행 만들기
            </Button>
          </div>
        ) : <p className="mt-3 text-xs text-gray-400">공유받은 강의라 행을 만들 수 없습니다.</p>}
      </div>
    );
  }

  if (!node) {
    return (
      <div className={`${panelCls} items-center justify-center p-6 text-center text-sm text-gray-400`}>
        {topSlot}
        왼쪽 진도에서 행을 고르세요.
      </div>
    );
  }

  const path = pathNames(ws.index, id);
  const pathText = [ws.data.name, ...path.slice(0, -1)].join(' › ');
  const query = ws.queryOf(id);
  const dirty = ws.isDirty(id);
  // 조건이 없는 줄 — 제목 줄이거나, 조건 줄의 초안이 조건을 뗀 경우(제목행으로 되돌린 초안)
  const unset = query == null;

  const header = (
    <div className="flex min-w-0 items-center gap-2">
      <RowName
        name={node.name}
        editable={owner}
        onRename={(n) => ws.rename(id, n)}
        autoEdit={renameRequest != null && String(renameRequest) === id}
        onAutoEditDone={onRenameRequestDone}
      />
      {dirty && <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">저장 전 변경</span>}
      <RowManageMenu
        node={node}
        index={ws.index}
        owner={owner}
        onReparent={onReparent}
        onConvert={onConvert}
        onDelete={onDelete}
        busy={ws.pendingWrites > 0}
        openMove={moveRequest != null && String(moveRequest) === id}
        onOpenMoveDone={onMoveRequestDone}
      />
    </div>
  );

  const searchInsp = unset ? null : inspectExpr(query, { mode: 'search' });
  const storeInsp = unset ? null : inspectExpr(query, { mode: 'store' });
  const canSearch = !unset && searchInsp.ok;
  let blockedReason = null;
  if (unset) blockedReason = '이 행에는 아직 조건이 없습니다 — 개념·라벨·난이도를 고르세요.';
  else if (hasEmptyGroup(query)) blockedReason = '검색 조건을 하나 이상 선택하세요.';
  else if (!searchInsp.ok) blockedReason = searchInsp.error.message;
  const saveState = ws.savingOf(id);
  const saveBlocked = !unset && dirty && !storeInsp.ok && searchInsp.ok
    ? (storeInsp.error.reason === 'contradiction' ? '서로 맞지 않는 조건이 있어 저장할 수 없습니다 — 검색은 할 수 있고, 그 묶음은 0건으로 셉니다.' : storeInsp.error.message)
    : null;
  // 조건을 뗀 초안은 [저장]이 아니라 아래 [제목행으로 저장](확인을 거치는 전환)으로 보낸다
  const canSave = owner && dirty && !unset && storeInsp.ok && saveState?.status !== 'saving';
  const result = ws.resultOf(id);
  const currentKey = unset ? null : exprKey(query);
  const serverError = currentKey == null ? null : (saveState?.status === 'error' && saveState.key === currentKey && saveState.details
    ? { details: saveState.details, message: saveState.message }
    : (result?.status === 'error' && result.errorKey === currentKey && result.errorDetails
      ? { details: result.errorDetails, message: result.error } : null));
  const fresh = ws.freshnessOf(id);
  const searching = result?.status === 'loading';
  const g = clampGroup(query, groupIdx);
  const searchedExpr = searchedExprOf(result); // 결과 칸과 같은 방식으로 「실제로 보낸 식」을 읽는다
  const conflict = ws.conflict && ws.conflict.nodeId === id ? ws.conflict : null;

  // ── 공통 조건(설계 FE-20260913 §3) — 서버 저장 식 기준. 초안은 공통 조건이 아니다(반영하려면 먼저 저장한다) ──
  const serverQueryOf = (k) => ws.index.byId.get(String(k))?.query ?? null;
  const descendantCount = descendantsOf(ws.index, id).length;
  const commonOf = commonOriginOf(ws.index, id, serverQueryOf);
  // 하위 행이 있는 행의 조건을 고쳤으면 저장이 곧 공통 조건을 바꾸는 일이다 — 저장하면서 하위 행에 반영하는 창을 연다
  const saveWithChildren = owner && descendantCount > 0 && dirty && !unset;
  const openApply = (mode) => setApplyState({
    mode,
    plan: planCommonApply({
      index: ws.index,
      parentId: id,
      common: mode === 'save' ? storeInsp?.normalized ?? null : node.query,
      previous: node.query,
      serverQueryOf,
      isDirty: (k) => ws.isDirty(k),
    }),
  });

  let statusText = unset ? '조건 없음' : '저장된 조건';
  if (autoPending) statusText = '검색 준비 중…';
  else if (searching) statusText = '검색 중…';
  else if (fresh === 'stale') statusText = '조건 변경됨 · 재검색 필요';
  else if (dirty) statusText = owner ? '저장 전 변경' : '이번 검색에만 변경';

  // [다시 검색] — 기다리지 않고 곧바로. 걸려 있던 0.8초 검색은 거둔다(같은 식을 두 번 보내지 않게)
  const runSearch = () => {
    if (!canSearch || searching) return;
    clearTimeout(autoRef.current.timer);
    autoRef.current.timer = null;
    setAutoPending(false);
    setAskSearch({ id, page: 1 });
  };
  // 개념·라벨 판의 [적용] — 초안에만 반영한다. 식이 바뀌었으니 검색은 0.8초 자동 검색이 맡는다
  const applyPicked = (q) => ws.setDraft(id, q);
  // 조건이 없는 줄의 [상세 조건]은 빈 묶음 하나로 연다 — 편집기는 식이 있어야 선다
  const openDetail = () => { if (unset) ws.setDraft(id, newExpr()); setDetailOpen(true); };
  // [상세 조건] 버튼 — 누를 때마다 여닫는다(오너 2026-09-14: 닫으려면 편집기 머리의 X 까지 가야 했다).
  // 「그 밖」 칩·중복 경고 링크는 위 openDetail(열기만) — 열린 채 눌러 닫히면 고치러 간 길이 사라진다
  const detailShown = detailOpen && !unset;
  const toggleDetail = () => { if (detailShown) setDetailOpen(false); else openDetail(); };

  const conditionBlock = (
    <>
      {topSlot}
      <p className="truncate text-[11px] text-gray-500" title={pathText}><MathPath names={[ws.data.name, ...path.slice(0, -1)]} /></p>
      {header}
      {!owner && (
        <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-500">
          공유받은 강의입니다 — 조건을 바꿔 검색해도 강의는 바뀌지 않습니다(이번 검색에만 변경). 영구 보관은 ⋯ 의 [내 강의로 복사].
        </p>
      )}
      {unset && !dirty && (
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">조건 없음</span>
          개념·라벨·난이도를 고르면 이 행의 조건이 됩니다.
        </p>
      )}
      {unset && dirty && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <span className="min-w-0 flex-1">초안에는 조건이 없습니다. 저장된 조건과 다릅니다.</span>
          {owner && node.query != null && (
            <Button size="xs" variant="ghost" onClick={() => onConvert(node)} disabled={ws.pendingWrites > 0}>제목행으로 저장</Button>
          )}
        </div>
      )}
      <CommonConditionBar
        ws={ws}
        node={node}
        id={id}
        query={query}
        dirty={dirty}
        owner={owner}
        names={names}
        onPull={(next) => ws.setDraft(id, next)}
        onOpenApply={() => openApply('apply')}
      />
      <QuickConditions
        // 줄마다 새로 세운다 — 앞 줄에서 연 팝오버의 임시 선택이 다음 줄에 적용되지 않게
        key={id}
        query={query}
        onChange={(q) => ws.setDraft(id, q)}
        onApplySearch={applyPicked}
        groupIndex={g}
        onGroupChange={setGroupIdx}
        dict={dict}
        labelNames={labelNames}
        onRememberLabels={onRememberLabels}
        onOpenDetail={openDetail}
        detailOpen={detailShown}
        onToggleDetail={toggleDetail}
        sheet={sheet}
        commonOf={commonOf}
        avoidRef={twoCol ? resultsRef : null}
      />
      {conflict && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <AlertTriangle className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{conflict.message} 위쪽 「저장 충돌 · 조건 비교」에서 쓸 조건을 고르세요.</span>
        </div>
      )}
      {saveState?.status === 'error' && <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600">{saveState.message}</p>}
      {saveBlocked && <p className="text-xs text-amber-700">{saveBlocked}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${fresh === 'stale' || dirty || autoPending ? 'font-medium text-amber-700' : 'text-gray-500'}`} role="status" aria-live="polite">{statusText}</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => ws.revertDraft(id)} disabled={!dirty} title="이 행 초안을 버리고 저장된 조건으로 되돌립니다">
            <RotateCcw className="mr-1 size-3.5" /> 조건 되돌리기
          </Button>
          {owner && (saveWithChildren ? (
            <Button size="sm" variant="ghost" onClick={() => openApply('save')} disabled={!canSave} loading={saveState?.status === 'saving'}>
              저장 · 하위 {descendantCount}개 행에 반영
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => ws.saveQuery(id)} disabled={!canSave} loading={saveState?.status === 'saving'}>
              저장
            </Button>
          ))}
          <Button size="sm" onClick={runSearch} disabled={!canSearch} loading={searching}>
            <Search className="mr-1 size-3.5" /> 다시 검색
          </Button>
        </div>
      </div>
      {applyState && (
        <CommonApplyDialog
          open
          mode={applyState.mode}
          parentName={node.name}
          plan={applyState.plan}
          names={names}
          onClose={() => setApplyState(null)}
          onSaveParent={() => ws.saveQuery(id)}
          onSaveOnly={() => ws.saveQuery(id)}
          onApplyRows={(rows, opts) => ws.applyCommonRows(rows, opts)}
        />
      )}
    </>
  );

  const detailBlock = detailOpen && !unset ? (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="min-w-0 flex-1 text-sm font-bold text-gray-900">상세 조건</h3>
        <button type="button" onClick={() => setDetailOpen(false)} aria-label="상세 조건 닫기" className={handleCls}><X className="size-4" /></button>
      </div>
      <p className="text-xs text-gray-500">각 묶음의 조건을 모두 만족하는 문제를 찾고, 묶음들의 결과를 합칩니다(묶음 안은 「그리고」, 묶음 사이는 「또는」).</p>
      <QueryEditor
        value={query}
        onChange={(q) => ws.setDraft(id, q)}
        dict={dict}
        labelNames={labelNames}
        onRememberLabels={onRememberLabels}
        activeGroup={g}
        onActiveGroupChange={setGroupIdx}
        serverError={serverError}
      />
      <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
        <span className="text-xs text-gray-500">{owner ? '저장 전 · 현재 행 초안에 적용됩니다.' : '이번 검색에만 적용됩니다.'}</span>
        <Button size="sm" className="ml-auto" onClick={() => { setDetailOpen(false); runSearch(); }} disabled={!canSearch}>적용·검색</Button>
      </div>
    </div>
  ) : null;

  const resultsBlock = (
    <SearchResults
      // 줄마다 새로 세운다 — 앞 줄의 목록 스크롤 위치가 다음 줄에 남지 않게
      key={id}
      result={result}
      freshness={fresh}
      canSearch={canSearch}
      blockedReason={blockedReason}
      onSearch={runSearch}
      onPage={(p) => setAskSearch({ id, page: p })}
      dict={dict}
      basisText={searchedExpr ? summarizeExpr(searchedExpr, names) : ''}
      query={searchedExpr}
      scrollMemory={scrollMemory || localScrollMemory.current}
      scrollKey={`${ws.data.syllabus_id}:${id}`}
      gutter={twoCol}
      autoPending={autoPending}
    />
  );

  // ── 두 칸 — 왼쪽 조건(남는 폭) | 오른쪽 결과(488px). 오른쪽 칸의 pr-4 와 결과 목록의 스크롤바 자리까지 488 안에 든다 ──
  if (twoCol) {
    return (
      <div
        ref={splitRef}
        data-layout="split"
        className="grid h-full min-h-0 gap-4 overflow-hidden rounded-3xl bg-white p-4 shadow-lg"
        style={{ gridTemplateColumns: `minmax(0, 1fr) ${VIEW_PANE_PX}px` }}
      >
        <div className="min-h-0 space-y-2 overflow-y-auto">
          {conditionBlock}
          {detailBlock && <div className="border-t border-gray-100 pt-3">{detailBlock}</div>}
        </div>
        <div ref={resultsRef} className="flex min-h-0 flex-col pr-4">{resultsBlock}</div>
      </div>
    );
  }

  // ── 위아래 — 위(고정)는 조건, 아래(스크롤)는 결과 또는 상세 조건(W04) ──
  return (
    <div ref={splitRef} data-layout="stack" className={`${panelCls} [@media(max-height:600px)]:overflow-y-auto`}>
      {/* 낮은 창·200% 확대에서는 패널 전체를 굴려 본문을 읽을 높이와 모든 조건 조작을 확보한다. */}
      <div className="max-h-[55%] shrink-0 space-y-2 overflow-y-auto border-b border-gray-100 p-4 [@media(max-height:600px)]:max-h-none [@media(max-height:600px)]:overflow-visible">
        {conditionBlock}
      </div>
      {/* 결과는 칸 안에서 스스로 굴리고(목록만 스크롤, 아래 한 줄은 제자리) 상세 편집기는 이 칸이 굴린다 */}
      <div className={`min-h-0 flex-1 p-4 [@media(max-height:600px)]:min-h-80 [@media(max-height:600px)]:shrink-0 ${detailBlock ? 'overflow-y-auto' : 'flex flex-col'}`}>
        {detailBlock || resultsBlock}
      </div>
    </div>
  );
}

export default WorkPanel;
